import CloseIcon from '@mui/icons-material/Close'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Box, IconButton, Paper, type PaperProps } from '@mui/material'
import { useState, type MouseEventHandler, type ReactNode } from 'react'
import { Collapse } from '../Collapse'

export type PanelProps = Omit<PaperProps, 'children'> & {
  actions?: ReactNode
  children?: ReactNode
  collapsible?: boolean
  defaultCollapsed?: boolean
  header?: ReactNode
  onClose?: MouseEventHandler<HTMLButtonElement>
  onCollapse?: (collapsed: boolean) => void
  tools?: ReactNode
}

export function Panel({
  actions,
  children,
  collapsible = true,
  defaultCollapsed = false,
  header,
  onClose,
  onCollapse,
  tools,
  ...props
}: PanelProps) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed)

  const handleCollapse: MouseEventHandler<HTMLButtonElement> = () => {
    const next = !collapsed
    setCollapsed(next)
    onCollapse?.(next)
  }

  return (
    <Paper
      {...props}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '6px',
        overflow: 'hidden',
        ...props.sx,
      }}
      variant="outlined"
    >
      <Box
        sx={{
          alignItems: 'center',
          borderBottom: children && !collapsed ? '1px solid' : 'none',
          borderColor: 'divider',
          display: 'flex',
          gap: '1rem',
          justifyContent: 'space-between',
          minHeight: 56,
          px: 2,
          py: 1.5,
        }}
      >
        <Box sx={{ flex: 1, fontSize: '1rem', fontWeight: 600 }}>{header}</Box>
        {actions ? <Box sx={{ alignItems: 'center', display: 'flex', gap: '0.5rem' }}>{actions}</Box> : null}
        <Box sx={{ alignItems: 'center', display: 'flex', gap: '0.25rem' }}>
          {tools}
          {collapsible ? (
            <IconButton aria-label={collapsed ? 'expand panel' : 'collapse panel'} onClick={handleCollapse} size="small">
              {collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
            </IconButton>
          ) : null}
          {onClose ? (
            <IconButton aria-label="close panel" onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          ) : null}
        </Box>
      </Box>

      <Collapse in={!collapsed}>
        <Box sx={{ p: 2 }}>{children}</Box>
      </Collapse>
    </Paper>
  )
}
